import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, useReducedMotion } from 'framer-motion';
import { EASE_OUT_EXPO } from '../../components/AnimatedSection';
import { pastorsData } from '../../data/pastorsData';
import PastorCard from './PastorCard';
import './PastorsSection.scss';

const MOBILE_BREAKPOINT = 768;
const AUTOPLAY_MS = 3500;
const RESUME_DELAY_MS = 5000;

const useIsMobile = () => {
    const [isMobile, setIsMobile] = useState(
        window.innerWidth < MOBILE_BREAKPOINT
    );

    useEffect(() => {
        const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
        const handler = (e) => setIsMobile(e.matches);
        mql.addEventListener('change', handler);
        return () => mql.removeEventListener('change', handler);
    }, []);

    return isMobile;
};

const PastorsSection = () => {
    const { t } = useTranslation();
    const prefersReducedMotion = useReducedMotion();
    const isMobile = useIsMobile();
    const carouselRef = useRef(null);

    useEffect(() => {
        if (!isMobile || prefersReducedMotion) return;
        const el = carouselRef.current;
        if (!el) return;

        let idx = 0;
        let paused = false;
        let resumeTimer;

        const scrollToCard = (i) => {
            const card = el.children[i];
            if (!card) return;
            el.scrollTo({
                left: card.offsetLeft - (el.offsetWidth - card.offsetWidth) / 2,
                behavior: 'smooth',
            });
        };

        const findCurrent = () => {
            const center = el.scrollLeft + el.offsetWidth / 2;
            let best = 0;
            let bestDist = Infinity;
            for (let i = 0; i < el.children.length; i++) {
                const c = el.children[i];
                const d = Math.abs(c.offsetLeft + c.offsetWidth / 2 - center);
                if (d < bestDist) { bestDist = d; best = i; }
            }
            return best;
        };

        const tick = setInterval(() => {
            if (paused) return;
            idx = (idx + 1) % pastorsData.length;
            scrollToCard(idx);
        }, AUTOPLAY_MS);

        const onTouchStart = () => {
            paused = true;
            clearTimeout(resumeTimer);
        };

        const onTouchEnd = () => {
            clearTimeout(resumeTimer);
            resumeTimer = setTimeout(() => {
                idx = findCurrent();
                paused = false;
            }, RESUME_DELAY_MS);
        };

        el.addEventListener('touchstart', onTouchStart, { passive: true });
        el.addEventListener('touchend', onTouchEnd, { passive: true });

        return () => {
            clearInterval(tick);
            clearTimeout(resumeTimer);
            el.removeEventListener('touchstart', onTouchStart);
            el.removeEventListener('touchend', onTouchEnd);
        };
    }, [isMobile, prefersReducedMotion]);

    const fadeUp = (delay, duration = 1.0) =>
        prefersReducedMotion
            ? {}
            : {
                  initial: { opacity: 0, y: 20 },
                  whileInView: { opacity: 1, y: 0 },
                  viewport: { once: true, amount: 0.25 },
                  transition: { duration, delay, ease: EASE_OUT_EXPO },
              };

    return (
        <section className="pastors-section" aria-labelledby="pastors-title">
            <div className="pastors-section__container">
                <motion.span className="pastors-section__label" {...fadeUp(0)}>
                    {t('serving_savior')}
                </motion.span>
                <motion.h2
                    id="pastors-title"
                    className="pastors-section__title"
                    {...fadeUp(0.15)}
                >
                    {t('meet_pastors')}
                </motion.h2>

                {isMobile ? (
                    <div className="pastors-section__carousel" ref={carouselRef}>
                        {pastorsData.map((pastor, index) => (
                            <PastorCard
                                key={pastor.id}
                                pastor={pastor}
                                index={index}
                                prefersReducedMotion={true}
                            />
                        ))}
                    </div>
                ) : (
                    <div className="pastors-section__grid">
                        {pastorsData.map((pastor, index) => (
                            <PastorCard
                                key={pastor.id}
                                pastor={pastor}
                                index={index}
                                prefersReducedMotion={prefersReducedMotion}
                            />
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
};

export default PastorsSection;
